import { Injectable } from '@angular/core';
import { createFrom, EventService, StateEventService } from '@spartacus/core';
import { ActionsSubject } from '@ngrx/store';
import {
  CustomCheckoutAddPaymentInfoEvent,
  CustomCheckoutAddShippingInfoEvent,
  CustomCheckoutBeginEvent,
  CustomCheckoutDeliveryModeEvent,
  CustomCheckoutPaymentModeEvent,
  CustomCheckoutStepsEvent,
} from './custom-checkout-steps.events';
import { Observable } from 'rxjs';
import { ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs/operators';
import { TagManagerFeatureService } from '../../tag-manager-feature.service';
import { LOAD_CHECKOUT_STEP_SUCCESS, SET_DELIVERY_MODE_SUCCESS } from './store/actions/custom-checkout.action';
import {
  SET_PAYMENT_TYPE_SUCCESS,
  SetPaymentTypeSuccess
} from "../../../../../custom/core/src/checkout/store/actions/custom-payment-types.action";
import {
  ADD_PAYMENT_INFO_SUCCESS,
  ADD_SHIPPING_INFO_SUCCESS,
  CHECKOUT_BEGIN,
} from "src/app/spartacus/custom/core/src/checkout/store/actions/checkout.action";
import { Cart } from "@spartacus/cart/base/root";
import { CustomCleanEcommerceEvent } from "../../common/custom-common.event";

@Injectable({
  providedIn: 'root',
})
export class CustomCheckoutStepsEventBuilder {
  constructor(
    protected actions: ActionsSubject,
    protected eventService: EventService,
    protected tagManagerFeatureService: TagManagerFeatureService,
  ) {
    this.register();
  }

  /**
   * Registers checkout events
   */
  protected register(): void {
    this.eventService.register(CustomCheckoutBeginEvent, this.buildBeginCheckoutEvent());
    this.eventService.register(CustomCheckoutAddShippingInfoEvent, this.buildAddShippingInfoEvent());
    this.eventService.register(CustomCheckoutAddPaymentInfoEvent, this.buildAddPaymentInfoEvent());
    this.eventService.register(CustomCheckoutStepsEvent, this.buildCheckoutStepsEvent());
    this.eventService.register(CustomCheckoutDeliveryModeEvent, this.buildDeliveryModeEvent());
    this.eventService.register(CustomCheckoutPaymentModeEvent, this.buildPaymentModeEvent());
  }

  /**
   * Builds begin checkout events
   */
  protected buildBeginCheckoutEvent(): Observable<CustomCheckoutBeginEvent> {
    return this.actions.pipe(
      ofType(CHECKOUT_BEGIN),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => action.payload.cart),
      map((activeCart: Cart) => {
        return createFrom(CustomCheckoutBeginEvent, {
          event: 'begin_checkout',
          ecommerce: {
            currency: activeCart.subTotal.currencyIso,
            value: activeCart.subTotal.value,
            coupon: '',
            items: activeCart.entries.map(item => this.tagManagerFeatureService.getCartProductItem(activeCart, item, item.quantity))
          },
        });
      }),
    );
  }

  /**
   * Builds add shipping events
   */
  protected buildAddShippingInfoEvent(): Observable<CustomCheckoutAddShippingInfoEvent> {
    return this.actions.pipe(
      ofType(ADD_SHIPPING_INFO_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => action.payload.cart),
      map((activeCart: Cart) => {
        return createFrom(CustomCheckoutAddShippingInfoEvent, {
          event: 'add_shipping_info',
          ecommerce: {
            currency: activeCart.subTotal.currencyIso,
            shipping_tier: activeCart.deliveryMode?.description,
            value: activeCart.subTotal.value,
            coupon: '',
            items: activeCart.entries.map(item => this.tagManagerFeatureService.getCartProductItem(activeCart, item, item.quantity))
          },
        });
      }),
    );
  }

  /**
   * Builds add payment events
   */
  protected buildAddPaymentInfoEvent(): Observable<CustomCheckoutAddPaymentInfoEvent> {
    return this.actions.pipe(
      ofType(ADD_PAYMENT_INFO_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => action.payload.cart),
      map((activeCart) => {
        return createFrom(CustomCheckoutAddPaymentInfoEvent, {
          event: 'add_payment_info',
          ecommerce: {
            currency: activeCart.subTotal.currencyIso,
            value: activeCart.subTotal.value,
            payment_type: activeCart.paymentMode?.name,
            coupon: '',
            items: activeCart.entries.map(item => this.tagManagerFeatureService.getCartProductItem(activeCart, item, item.quantity))
          },
        });
      }),
    );
  }

  /**
   * Builds the purchase events
   */
  protected buildCheckoutStepsEvent(): Observable<CustomCheckoutStepsEvent> {
    return this.actions.pipe(
      ofType(LOAD_CHECKOUT_STEP_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => action.payload),
      map(({step, entries, optionInformation, giftCard}) => {
        return createFrom(CustomCheckoutStepsEvent, {
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: {
                step,
                option: optionInformation?.length ? optionInformation : '',
              },
              products: entries?.length ? this.tagManagerFeatureService.getOrderEntries(entries) : [],
            },
          },
        });
      }),
    );
  }

  /**
   * Builds the delivery mode events
   */
  protected buildDeliveryModeEvent(): Observable<CustomCheckoutDeliveryModeEvent> {
    return this.actions.pipe(
      ofType(SET_DELIVERY_MODE_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => {
        return action.payload;
      }),
      map((selectedModeId: string) => {
        return createFrom(CustomCheckoutDeliveryModeEvent, {
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: {
                step: 2,
                option: selectedModeId?.length ? selectedModeId : '',
              },
            },
          },
        });
      }),
    );
  }

  /**
   * Builds the payment mode events
   */
  protected buildPaymentModeEvent(): Observable<CustomCheckoutPaymentModeEvent> {
    return this.actions.pipe(
      ofType(SET_PAYMENT_TYPE_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: SetPaymentTypeSuccess) => action.payload),
      map((paymentType: any) => {
        return createFrom(CustomCheckoutPaymentModeEvent, {
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: {
                step: 3,
                option: paymentType?.length ? paymentType : '',
              },
            },
          },
        });
      }),
    );
  }
}





